import { useId, useState } from 'react'
import type { components } from '@/api/schema'
import {
  ClassificationRequestError,
  useCreateMailboxResource,
  useUpdateMailboxResource,
  useDeleteMailboxResource,
  type MailboxResourceKind,
} from '@/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { resourceName } from './resource-name'

type Resource = components['schemas']['ClassificationResourceRead']
type Catalog = components['schemas']['ClassificationResourcesResponse']
export type ResourceEditor = {
  kind: MailboxResourceKind
  mode: 'create' | 'edit' | 'delete'
  resource?: Resource
}

export function ResourceDialog({
  inboxId,
  editor,
  catalog,
  allowed,
  onClose,
  onRefresh,
}: {
  inboxId: string
  editor: ResourceEditor
  catalog: Catalog
  allowed: boolean
  onClose: () => void
  onRefresh: () => Promise<unknown>
}) {
  const prefix = useId()
  const create = useCreateMailboxResource(inboxId)
  const update = useUpdateMailboxResource(inboxId)
  const remove = useDeleteMailboxResource(inboxId)
  const { resource, kind, mode } = editor
  const [name, setName] = useState(resource?.name ?? '')
  const [color, setColor] = useState(resource?.color ?? 'none')
  const [parentId, setParentId] = useState('')
  const [deleteContents, setDeleteContents] = useState(false)
  const [error, setError] = useState('')
  const [nameError, setNameError] = useState(false)
  const [unconfirmed, setUnconfirmed] = useState(false)
  const key =
    kind === 'label' ? 'labels' : kind === 'folder' ? 'folders' : 'categories'
  const management = catalog.management[key]
  const pending = create.isPending || update.isPending || remove.isPending
  const title = `${mode === 'create' ? 'Create' : mode === 'delete' ? 'Delete' : kind === 'category' ? 'Edit' : 'Rename'} ${kind}`
  const submit = async () => {
    setError('')
    try {
      if (mode === 'delete' && resource)
        await remove.mutateAsync({
          kind,
          id: resource.id,
          deleteContents: kind === 'folder' && deleteContents,
        })
      else if (mode === 'edit' && resource)
        await update.mutateAsync({
          kind,
          id: resource.id,
          body: kind === 'category' ? { color } : { name: name.trim() },
        })
      else
        await create.mutateAsync({
          kind,
          body: {
            name: name.trim(),
            ...(kind === 'folder' ? { parent_id: parentId || null } : {}),
            ...(kind === 'category' ? { color } : {}),
          },
        })
      onClose()
    } catch (failure) {
      const status =
        failure instanceof ClassificationRequestError ? failure.status : null
      const code =
        failure instanceof ClassificationRequestError ? failure.code : null
      const uncertain =
        code === 'unknown_outcome' || status === null || status >= 500
      setNameError(code === 'reserved_label_name')
      setUnconfirmed(
        uncertain ||
          [
            'target_missing',
            'resource_forbidden',
            'unsupported_operation',
          ].includes(code ?? ''),
      )
      setError(
        uncertain
          ? 'We couldn’t confirm the change. Reload your labels and folders before trying again.'
          : code === 'resource_in_use'
            ? 'It’s still ticked under Allowed actions. Untick it and save first.'
            : code === 'missing_scopes' ||
                code === 'reconnect_required' ||
                status === 401 ||
                status === 403
              ? 'Reconnect this inbox to manage its labels and folders.'
              : code === 'target_missing'
                ? 'This no longer exists. Reload your labels and folders.'
                : code === 'resource_forbidden' ||
                    code === 'unsupported_operation'
                  ? 'This can’t be changed here.'
                  : code === 'rate_limited'
                    ? 'Your mailbox provider is limiting requests. Wait a moment and try again.'
                    : status === 429
                      ? 'Too many requests. Wait a moment, then try again.'
                      : code === 'reserved_label_name'
                        ? 'Gmail already has a built-in label with this name. Choose a different label name.'
                        : 'The change couldn’t be saved. Try again.',
      )
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose()
      }}
    >
      <DialogContent className="sentry-block ph-no-capture max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === 'delete'
              ? resource?.name
              : mode === 'create'
                ? `Creates a new ${kind} in your inbox.`
                : resource?.name}
          </DialogDescription>
        </DialogHeader>
        {mode === 'delete' ? (
          <div className="space-y-4 text-sm">
            <p>
              {management.delete_effect === 'folder_and_contents'
                ? 'This folder, its email, and its subfolders will be deleted.'
                : management.delete_effect === 'label_detach'
                  ? 'This label will be removed from messages. The messages are kept.'
                  : 'This category will be removed from Outlook’s category list. Existing email category names are kept.'}
            </p>
            {kind === 'folder' && (
              <>
                {(resource?.item_count != null ||
                  resource?.child_count != null) && (
                  <p className="text-muted-foreground">
                    {resource?.item_count != null
                      ? `${resource.item_count} ${resource.item_count === 1 ? 'item' : 'items'}`
                      : ''}
                    {resource?.item_count != null &&
                    resource?.child_count != null
                      ? ' · '
                      : ''}
                    {resource?.child_count != null
                      ? `${resource.child_count} ${resource.child_count === 1 ? 'subfolder' : 'subfolders'}`
                      : ''}
                  </p>
                )}
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 shrink-0 accent-primary"
                    checked={deleteContents}
                    onChange={(event) =>
                      setDeleteContents(event.target.checked)
                    }
                  />
                  <span>Delete this folder and its contents</span>
                </label>
              </>
            )}
            {allowed && (
              <p role="status">
                Uncheck this resource in permissions and save before deleting.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {(mode === 'create' || kind !== 'category') && (
              <div className="space-y-2">
                <Label htmlFor={`${prefix}-name`}>Name</Label>
                <Input
                  id={`${prefix}-name`}
                  maxLength={255}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value)
                    if (nameError) {
                      setError('')
                      setNameError(false)
                    }
                  }}
                />
              </div>
            )}
            {kind === 'folder' && mode === 'create' && (
              <div className="space-y-2">
                <Label htmlFor={`${prefix}-parent`}>Parent folder</Label>
                <select
                  className="classification-select"
                  id={`${prefix}-parent`}
                  value={parentId}
                  onChange={(event) => setParentId(event.target.value)}
                >
                  <option value="">Top level</option>
                  {catalog.folders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {resourceName(folder, catalog.folders)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {kind === 'category' && (
              <div className="space-y-2">
                <Label htmlFor={`${prefix}-color`}>Color</Label>
                <select
                  id={`${prefix}-color`}
                  className="classification-select"
                  value={color}
                  onChange={(event) => setColor(event.target.value)}
                >
                  {management.colors?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={onClose}>
            Cancel
          </Button>
          {unconfirmed ? (
            <Button
              onClick={async () => {
                await onRefresh()
                onClose()
              }}
            >
              Reload
            </Button>
          ) : (
            <Button
              variant={mode === 'delete' ? 'destructive' : 'default'}
              disabled={
                pending ||
                (mode === 'delete'
                  ? allowed || (kind === 'folder' && !deleteContents)
                  : (mode === 'create' || kind !== 'category') && !name.trim())
              }
              onClick={submit}
            >
              {pending
                ? 'Saving…'
                : mode === 'delete'
                  ? `Delete ${kind}`
                  : mode === 'create'
                    ? `Create ${kind}`
                    : 'Save changes'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
